import {useState} from 'react'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {MRT_API_BASEPATH} from '../ssr/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    useCustomPreference,
    useCustomPreferenceMarketless,
    useBooleanCustomPreference,
} from '../hooks/use-custom-preference'

import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

export const useHandleEmployeeBalance = () => {
    const {data: customer} = useCurrentCustomer()
    const [employeeError, setEmployeeError] = useState(null) // State to store errors
    const [employeeData, setEmployeeData] = useState(null) // State to store employee data
    const appOrigin = useAppOrigin(false)
    const {currency} = useCurrency()
    const employeeBalancePrefData = useCustomPreference('EmployeeProgram_Data') // Fetch employee program preferences
    const employeeBalanceRates = useCustomPreferenceMarketless('EmployeeProgram_Rates') // Fetch exchange rates
    const isEmployeeProgramExcluded = useBooleanCustomPreference(
        'Excluded_Countries_EmployeeProgram',
    ) // Country-level exclusion flag

    // Function to fetch employee balance data
    const handleEmployeeBalance = async () => {
        // If country is excluded from employee program, skip API entirely.
        if (isEmployeeProgramExcluded) {
            return {
                success: true,
                skipped: true,
                reason: 'country-excluded',
                message: 'Employee program disabled for this country. Skipping balance fetch.',
            }
        }
        try {
            // Encode rates as a query parameter
            const ratesQueryParam = employeeBalanceRates
                ? encodeURIComponent(JSON.stringify(employeeBalanceRates))
                : ''

            // Fetch employee balance data from the OMS Employee API
            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-employee-balance?customerId=${customer?.c_gigyaId}&sfAccountEmail=${customer?.email}&currency=${currency}&rates=${ratesQueryParam}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )
            const result = await response.json()
            if (result.success) {
                setEmployeeData(result) // Store the result in state
                return result
            } else {
                setEmployeeError('Employee balance data retrieve failed: ' + result.error)
                console.error('Error during retrieving OMS data:', employeeError)
            }
        } catch (error) {
            setEmployeeError('Error during retrieving OMS data: ' + error.message)
            console.error('Error during retrieving OMS data:', employeeError)
        }
    }

    // Function to validate employee balance and product quantities
    const checkEmployeeBalanceAndProductQuantity = (basket, employeeData) => {
        // If country excluded, skip validation.
        if (isEmployeeProgramExcluded) {
            return {
                success: true,
                skipped: true,
                reason: 'country-excluded',
                message: 'Employee program disabled for this country. Validation skipped.',
            }
        }
        // Hard failure only if OMS employee data is missing.
        if (!employeeData) {
            return {
                success: false,
                errorType: 'employee-data-missing',
                message: 'Employee data is missing.',
            }
        }

        // If preference config is absent, skip validation entirely (treat as no employee program for this market).
        const prefInvalid = !employeeBalancePrefData || employeeBalancePrefData === 'true'
        if (prefInvalid) {
            return {
                success: true,
                skipped: true,
                reason: 'no-preference-config-or-invalid',
                message: 'Employee program preference missing or invalid. Validation skipped.',
            }
        }
        const totalAmountWithTax = employeeData.totalAmountWithTax || 0
        const balance = parseFloat(employeeBalancePrefData.balance)

        // Step 2: Count material groups from order summaries
        const orderSummaries = employeeData?.response?.records || []
        //general list where save all the final counting of the material groups
        const materialGroupCount = {}
        if (employeeBalancePrefData.products) {
            Object.keys(employeeBalancePrefData.products).forEach((materialCode) => {
                materialGroupCount[materialCode] = 0
            })
        }
        if (orderSummaries) {
            orderSummaries.forEach((orderSummary) => {
                const orderItemSummary = orderSummary?.OrderItemSummaries?.records || []
                orderItemSummary.forEach((item) => {
                    // ASSUMPTION: second-hand products have a material group code starting with '2' and then same as the original product
                    // Normalize material group: treat codes starting with '2' (e.g., '2HHCMFA')
                    // as contributions to their base group ('HHCMFA').
                    const rawGroup = item.ProductMaterialGroup__c
                    const materialGroup = rawGroup?.startsWith('2') ? rawGroup.slice(1) : rawGroup
                    if (
                        materialGroupCount[materialGroup] !== undefined &&
                        materialGroupCount[materialGroup] >= 0
                    ) {
                        // If the material group is found in the preference data, add the quantity
                        // Use 0 if quantity is explicitly 0; fallback to 1 only if null/undefined
                        const qty = item.Quantity ?? 1
                        materialGroupCount[materialGroup] += Number(qty)
                    }
                })
            })
        }

        // Step 3: Count material groups and prices from the basket
        var totalAmountInBasket = 0
        var employeeDiscountInBasket = false
        var materialGroupInBasket = false
        basket.productItems.forEach((product) => {
            var hasEmployeeSalesDiscount = product?.priceAdjustments?.some(
                (adjustment) =>
                    adjustment.appliedDiscount &&
                    adjustment.appliedDiscount.priceBookId &&
                    adjustment.appliedDiscount.priceBookId.includes('employee-sales'),
            )
            if (hasEmployeeSalesDiscount) {
                employeeDiscountInBasket = true
                totalAmountInBasket += product.priceAfterItemDiscount || product.price
                // Normalize basket material group as well (2XXX -> XXX)
                const rawGroup = product.c_MaterialGroup
                const materialGroup = rawGroup?.startsWith('2') ? rawGroup.slice(1) : rawGroup
                if (
                    materialGroup &&
                    materialGroupCount[materialGroup] !== undefined &&
                    materialGroupCount[materialGroup] >= 0
                ) {
                    // If the material group is found in the basket, add the quantity
                    materialGroupInBasket = true
                    materialGroupCount[materialGroup] += product.quantity
                }
            }
        })

        // Step 4: Validate all product quantities and balance
        if (employeeDiscountInBasket) {
            const materialGroups = employeeBalancePrefData.products
            var allProductsAvailable = true
            const insufficientProducts = []
            if (materialGroupInBasket) {
                for (const materialCode in materialGroups) {
                    const availabilityMax = parseInt(materialGroups[materialCode])
                    const requestedCount = parseInt(materialGroupCount[materialCode] || 0)
                    if (requestedCount > availabilityMax) {
                        allProductsAvailable = false
                        insufficientProducts.push({
                            materialCode,
                            required: availabilityMax,
                            requested: requestedCount,
                        })
                    }
                }
            }
            var total = parseFloat(totalAmountWithTax) || 0
            if (total + totalAmountInBasket > balance) {
                return {
                    success: false,
                    errorType: 'balance-exceeded',
                    message:
                        'The total amount of the discount price you are buying exceeds the available balance.',
                }
            }
            if (!allProductsAvailable && insufficientProducts.length > 0) {
                return {
                    success: false,
                    errorType: 'material-group-quantity-exceeded',
                    message: 'Insufficient products available.',
                    insufficientProducts,
                }
            }
        }
        return {
            success: true,
            errorType: null,
            message: 'Balance and product quantities are sufficient.',
        }
    }

    return {
        handleEmployeeBalance,
        checkEmployeeBalanceAndProductQuantity,
        employeeBalancePrefData,
        employeeError,
        employeeData,
        isEmployeeProgramExcluded,
    }
}
